import { SlotService } from '../state/types';
import {
  AddonLine,
  getParticipantAddonLines,
} from './getParticipantAddonLines';
import {
  getBasePrice,
  getParticipantPayment,
  isBuyingPlan,
  isPlanPayment,
  toFinitePrice,
} from './participantPayment';

export type ParticipantSummary = {
  variantName?: string;
  planName?: string;
  isCoveredByPlan: boolean;
  addonLines: AddonLine[];
  sessionPrice: number;
  total: number;
  showTotal: boolean;
  currency: string;
};

export const getParticipantSummary = (
  slotService: SlotService,
  participantIndex: number,
): ParticipantSummary | undefined => {
  const participant = slotService.participants?.[participantIndex];
  if (!participant) {
    return undefined;
  }
  const { currency } = slotService.paymentDetails;
  const variantName = participant.selectedVariant?.choices?.[0]?.custom;
  const payment = getParticipantPayment(slotService, participantIndex);
  const planName = isPlanPayment(payment)
    ? payment.selectedPaymentOption?.label
    : undefined;
  const addonLines = getParticipantAddonLines({
    ...slotService,
    participants: [participant],
  });
  const addonsTotal = addonLines.reduce(
    (sum, { price, quantity }) => sum + price * quantity,
    0,
  );
  const isCoveredByPlan =
    !!planName &&
    (isBuyingPlan(payment) ||
      !!slotService.memberships?.eligibleMemberships?.some(
        (membership) => membership?.id === payment.selectedPaymentOption?.id,
      ));
  const sessionPrice = isCoveredByPlan
    ? 0
    : getParticipantSessionPrice(slotService, participant.id, variantName);

  return {
    variantName,
    planName,
    isCoveredByPlan,
    addonLines,
    sessionPrice,
    total: sessionPrice + addonsTotal,
    showTotal: !isCoveredByPlan || addonsTotal > 0,
    currency,
  };
};

const getParticipantSessionPrice = (
  slotService: SlotService,
  participantId: string,
  variantName: string | undefined,
) => {
  const { paymentDetails, dynamicPriceInfo } = slotService;
  const calculatedPrice =
    paymentDetails.participantPricing?.[participantId]?.price;
  if (calculatedPrice !== undefined) {
    return calculatedPrice;
  }
  const variantPrice = dynamicPriceInfo?.paymentDetails?.find(
    ({ choice }) => choice === variantName,
  )?.price;
  return toFinitePrice(variantPrice?.value) ?? getBasePrice(slotService);
};
